import { useNotification, usePermissions } from 'hooks'
import { getLocation } from '../api'
import { useParams } from 'react-router-dom'
import s from './Location.modules.scss'
import { InfoItems } from 'components/core-ui/InfoItems'
import { Button, SimpleListTable } from '@fullscreendigital/carrefour-dex-fe'
import { useEffect, useState } from 'react'
import { getLocationChildrenRows } from './helpers'
import { AddActionIcon, DeleteActionIcon, EditActionIcon } from 'icons'
import { Box } from '@mui/material'
import { LocationChildFormModal } from './LocationChildFormModal'
import { DisableConfirmationModal } from './DisableConfirmationModal'
import { roles } from '../constants'

const Location = () => {
  const [rows, setRows] = useState([])
  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDisableModalOpen, setIsDisableModalOpen] = useState<boolean>(false)
  const [parentId, setParentId] = useState<number>(0)
  const [editItemId, setEditItemId] = useState<number>(0)
  const [disabledItemId, setDisabledItemId] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)

  const { id } = useParams()

  const { showNotification } = useNotification()

  const { canCreate, canEdit, canDelete } = usePermissions(roles)

  const { data: location } = getLocation(
    showNotification,
    +id,
    shouldRefreshData,
    setShouldRefreshData
  )

  useEffect(() => {
    if (location?.locationUnit) {
      setRows(
        getLocationChildrenRows(
          location.locationUnit.name,
          location.locationUnit.childList ?? [],
          0,
          addChild,
          editItem,
          deactivateItem
        )
      )
    }
  }, [location?.locationUnit])

  const openNewModal = () => setIsNewModalOpen(true)
  const closeNewModal = () => setIsNewModalOpen(false)

  const openEditModal = () => setIsEditModalOpen(true)
  const closeEditModal = () => setIsEditModalOpen(false)

  const openDisableModal = () => setIsDisableModalOpen(true)
  const closeDisableModal = () => setIsDisableModalOpen(false)

  const addChild = (parentId: number) => {
    setParentId(parentId)
    openNewModal()
  }

  const editItem = (rowId: number) => {
    setEditItemId(rowId)
    openEditModal()
  }

  const deactivateItem = (rowId: number) => {
    setDisabledItemId(rowId)
    openDisableModal()
  }

  return (
    <div className={s.mainContainer}>
      {canCreate && (
        <LocationChildFormModal
          isModalOpen={isNewModalOpen}
          closeModal={closeNewModal}
          parentId={parentId}
          setShouldRefreshData={setShouldRefreshData}
          shouldRefreshData={shouldRefreshData}
        />
      )}
      {canCreate && (
        <LocationChildFormModal
          isModalOpen={isEditModalOpen}
          closeModal={closeEditModal}
          editItemId={editItemId}
          isEdit
          setShouldRefreshData={setShouldRefreshData}
          shouldRefreshData={shouldRefreshData}
        />
      )}
      {canDelete && (
        <DisableConfirmationModal
          isModalOpen={isDisableModalOpen}
          closeModal={closeDisableModal}
          id={disabledItemId}
          setShouldRefreshData={setShouldRefreshData}
          shouldRefreshData={shouldRefreshData}
        />
      )}
      <InfoItems
        itemsPerRow={3}
        items={
          location
            ? [
                { name: location.name, label: 'Location name:' },
                { name: location.name, label: 'Location code:' },
                { name: location.legalEntity.name, label: 'Legal entity:' },
                { name: location.subtype.type.name, label: 'Type:' },
                ...(location.regionList?.length
                  ? [
                      {
                        name: location.regionList?.map(
                          region => `${region.region.category.name} - ${region.region.name}`
                        ),
                        label: 'Regions:'
                      }
                    ]
                  : []),
                { name: location.locationStatus?.name, label: 'Location status:' },
                { name: location.locationUsageAddress.address.completeAddress, label: 'Address:' },
                ...(location?.externalIdList?.map(externalId => ({
                  name: externalId.value,
                  label: `${externalId.system.name}:`
                })) || [])
              ]
            : []
        }
      />
      {canDelete && (
        <Box className={s.addBtnContainer}>
          <Button
            variant="contained"
            label="Add child"
            onClick={() => addChild(location?.locationUnit?.id)}
          />
        </Box>
      )}
      <SimpleListTable
        rows={rows}
        rowActions={
          canEdit || canCreate || canDelete
            ? [
                canEdit && {
                  label: 'Edit',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  backgroundColor: '#16a085',
                  desktopIcon: <EditActionIcon />,
                  onClick: (_, rowId) => editItem(+rowId)
                },
                canCreate && {
                  label: 'Add child',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  backgroundColor: '#0970E6',
                  desktopIcon: <AddActionIcon />,
                  onClick: (e, rowId) => addChild(+rowId)
                },
                canDelete && {
                  label: 'Change status',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  backgroundColor: '#d9534f',
                  desktopIcon: <DeleteActionIcon />,
                  onClick: (_e, rowId) => deactivateItem(+rowId)
                }
              ]
            : []
        }
        rowActionsWidth="370px"
      />
    </div>
  )
}

export default Location
